import React from "react";
import Section from "./Section";

export default function HTML_Quotations() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">💬 HTML Quotations and Citations</h1>

      {/* 🔹 Introduction */}
      <p className="tutorial-intro">
        HTML provides special <strong>quotation and citation elements</strong>{" "}
        to mark quoted, cited, or referred text. These tags help define meaning,
        improve accessibility, and make text semantically clear.
      </p>

      {/* 🔹 List of Quotation & Citation Tags */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
          borderRadius: "12px",
          padding: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <h3
          style={{
            color: "#16a34a",
            fontWeight: 700,
            fontSize: "1.5rem",
            marginBottom: "0.5rem",
          }}
        >
          📜 Common HTML Quotation & Citation Tags
        </h3>
        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code_1">&lt;q&gt;</code> – Short inline
            quotation
          </li>
          <li>
            <code className="highlight_code_1">&lt;blockquote&gt;</code> – Long
            (block) quotation
          </li>
          <li>
            <code className="highlight_code_1">&lt;abbr&gt;</code> –
            Abbreviation or acronym
          </li>
          <li>
            <code className="highlight_code_1">&lt;address&gt;</code> – Contact
            or author information
          </li>
          <li>
            <code className="highlight_code_1">&lt;cite&gt;</code> – Citation
            for a work (book, article, etc.)
          </li>
          <li>
            <code className="highlight_code_1">&lt;bdo&gt;</code> –
            Bi-directional text override
          </li>
        </ul>
      </div>

      {/* Section 1️⃣ — Short Quotation */}
      <Section
        title="1️⃣ Short Quotations — &lt;q&gt;"
        html={`<p>He said, <q>Learning never stops.</q></p>`}
        description="The <q> tag is used for short, inline quotations. Browsers typically add quotation marks automatically."
      />

      {/* Section 2️⃣ — Block Quotation */}
      <Section
        title="2️⃣ Block Quotations — &lt;blockquote&gt;"
        html={`<blockquote cite="https://www.wikipedia.org">
  The World Wide Web (WWW) is a system of interlinked hypertext documents accessed via the Internet.
</blockquote>`}
        description="The <blockquote> tag defines a section quoted from another source. The cite attribute can specify the source URL."
      />

      {/* Section 3️⃣ — Abbreviation */}
      <Section
        title="3️⃣ Abbreviation — &lt;abbr&gt;"
        html={`<p><abbr title="Cascading Style Sheets">CSS</abbr> is used for styling HTML documents.</p>`}
        description="The <abbr> tag shows the full form of abbreviations or acronyms when hovered over — improving readability and accessibility."
      />

      {/* Section 4️⃣ — Citation */}
      <Section
        title="4️⃣ Citation — &lt;cite&gt;"
        html={`<p>The book <cite>Clean Code</cite> is written by Robert C. Martin.</p>`}
        description="The <cite> tag is used to reference the title of a creative work like books, movies, or research papers."
      />

      {/* Section 5️⃣ — Address */}
      <Section
        title="5️⃣ Address — &lt;address&gt;"
        html={`<address>
  Written by Sanjana Tiwari.<br/>
  Visit us at: example.com<br/>
  Email: contact@example.com
</address>`}
        description="The <address> tag defines contact information for the author or owner of a webpage."
      />

      {/* Section 6️⃣ — Bi-Directional Override */}
      <Section
        title="6️⃣ Bi-Directional Override — &lt;bdo&gt;"
        html={`<p>Normal text: Hello</p>
<p><bdo dir="rtl">Reversed text: Hello</bdo></p>`}
        description="The <bdo> tag allows you to override the default text direction — useful for languages like Arabic or Hebrew."
      />

      {/* ✅ Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary — Quotation and Citation Tags</h2>
        <ul className="tutorial-list-points">
          <li>
            <b>&lt;q&gt;</b> → Short inline quote
          </li>
          <li>
            <b>&lt;blockquote&gt;</b> → Long (block-level) quote
          </li>
          <li>
            <b>&lt;abbr&gt;</b> → Abbreviation full form on hover
          </li>
          <li>
            <b>&lt;cite&gt;</b> → Title of a creative work
          </li>
          <li>
            <b>&lt;address&gt;</b> → Author or contact info
          </li>
          <li>
            <b>&lt;bdo&gt;</b> → Reverses or overrides text direction
          </li>
        </ul>
      </div>
    </div>
  );
}
